import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import React, { lazy, Suspense, useEffect } from 'react';
import { lazyWithRetry } from '../utils/lazyWithRetry';

// EAGER IMPORTS: Critical path - Layout components and HomePage
import MainLayout from '../components/layout/MainLayout';
import NoFooterLayout from '../components/layout/NoFooterLayout';
import PublicLayout from '../components/layout/PublicLayout';
import PublicHeaderLayout from '../components/layout/PublicHeaderLayout';
import ProtectedRoute from '../components/common/ProtectedRoute';
import UserRecognitionWrapper from '../components/common/UserRecognitionWrapper';
import ScrollToTop from '../components/common/ScrollToTop';
import HomePage from '../pages/HomePage';

// Build target: "public" strips admin routes entirely.
// Vite replaces import.meta.env.VITE_BUILD_TARGET at compile time, making this
// a constant that enables dead-code elimination of all admin imports below.
const isPublicBuild = import.meta.env.VITE_BUILD_TARGET === 'public';

// ADMIN IMPORTS: Gated behind !isPublicBuild so Vite tree-shakes them in public builds.
// When isPublicBuild is true (compile-time constant), the ternary false-branch is dead
// code and Rollup drops the dynamic import() along with the chunk.
const AdminLayout = !isPublicBuild ? lazyWithRetry(() => import('../components/layout/AdminLayout')) : null;
const AdminProtectedRoute = !isPublicBuild ? lazyWithRetry(() => import('../components/common/AdminProtectedRoute')) : null;
const AdminDashboard = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/Dashboard')) : null;
const UserManagement = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/UserManagement')) : null;
const UserDetail = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/UserDetail')) : null;
const CountryPricing = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/CountryPricing')) : null;
const CountryPricingRates = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/CountryPricingRates')) : null;
const OrderManagement = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/OrderManagement')) : null;
const SubscriptionManagement = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/SubscriptionManagement')) : null;
const Analytics = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/Analytics')) : null;
const Settings = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/Settings')) : null;
const DatabaseManagement = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/DatabaseManagement')) : null;
const DatabaseQuery = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/DatabaseQuery')) : null;
const Logs = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/Logs')) : null;
const TestFunctions = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/TestFunctions')) : null;
const EsimCdrConnector = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/EsimCdrConnector')) : null;
const UserReset = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/UserReset')) : null;
const CdrView = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/CdrView')) : null;
const SubscriptionTesting = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/SubscriptionTesting')) : null;
const TestClocks = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/TestClocks')) : null;
const ESIMManagement = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/ESIMManagement')) : null;
const ESIMReport = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/ESIMReport')) : null;
const MonthlyReportDetail = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/MonthlyReportDetail')) : null;
const PLMNDirectory = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/PLMNDirectory')) : null;
const CDRMonitoring = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/CDRMonitoring')) : null;
const FraudMonitor = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/FraudMonitor')) : null;
const PixelDeviceSearch = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/PixelDeviceSearch')) : null;
const OAuthClients = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/OAuthClients')) : null;
const BonusPrograms = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/BonusPrograms')) : null;
const BonusProgramForm = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/BonusProgramForm')) : null;
const BonusProgramStats = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/BonusProgramStats')) : null;
const BonusSimulator = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/BonusSimulator')) : null;
const ReferralManagement = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/ReferralManagement')) : null;
const Mail = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/Mail')) : null;
const MailConfiguration = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/MailConfiguration')) : null;
const MailTemplates = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/MailTemplates')) : null;
const MailTriggers = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/MailTriggers')) : null;
const MailWrapper = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/MailWrapper')) : null;
const MailRecent = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/MailRecent')) : null;
const MailQueue = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/MailQueue')) : null;
const MailSupport = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/MailSupport')) : null;
const ZohoMailSettings = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/mail/ZohoMailSettings')) : null;
const AIKnowledgeDashboard = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/knowledge/AIKnowledgeDashboard')) : null;
const ChatLogs = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/knowledge/ChatLogs')) : null;
const KnowledgeBase = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/knowledge/KnowledgeBase')) : null;
const ChatAnalysis = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/knowledge/ChatAnalysis')) : null;
const OpenAIStorage = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/knowledge/OpenAIStorage')) : null;
const AuthenticatedSessions = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/knowledge/AuthenticatedSessions')) : null;
const AIFunctionDocs = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/knowledge/AIFunctionDocs')) : null;
const E2EResults = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/E2EResults')) : null;
const PlatformUpdates = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/PlatformUpdates')) : null;
const ApiDocumentation = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/ApiDocumentation')) : null;
const DocsAdmin = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/DocsAdmin')) : null;
const VoucherCodes = !isPublicBuild ? lazyWithRetry(() => import('../pages/admin/VoucherCodes')) : null;

// LAZY IMPORTS: All other pages for code splitting
// User pages
const Dashboard = lazyWithRetry(() => import('../pages/user/Dashboard'));
const ProfileSettings = lazyWithRetry(() => import('../pages/user/ProfileSettings'));
const BillingPayment = lazyWithRetry(() => import('../pages/user/BillingPayment'));
const UsageData = lazyWithRetry(() => import('../pages/user/UsageData'));
const EsimsPage = lazyWithRetry(() => import('../pages/user/EsimsPage'));
const Wrapped = lazyWithRetry(() => import('../pages/user/Wrapped'));
const Referrals = lazyWithRetry(() => import('../pages/user/Referrals'));
const SpeedTest = lazyWithRetry(() => import('../pages/user/SpeedTest'));
const SpeedTestHistory = lazyWithRetry(() => import('../pages/user/SpeedTestHistory'));
const FeatureRequests = lazyWithRetry(() => import('../pages/user/FeatureRequests'));
const WorldCupGame = lazyWithRetry(() => import('../pages/game/WorldCupGame'));
const PurchasePlan = lazyWithRetry(() => import('../pages/archive/PurchasePlan'));
const PaymentConfirm = lazyWithRetry(() => import('../pages/newuser/PaymentConfirm'));
const PurchaseSuccess = lazyWithRetry(() => import('../pages/user/PurchaseSuccess'));
const CookieTest = lazyWithRetry(() => import('../pages/user/CookieTest'));
const AuthDebug = lazyWithRetry(() => import('../pages/debug/AuthDebug'));
const LocalStorageDebug = lazyWithRetry(() => import('../pages/debug/LocalStorageDebug'));

// Auth pages
const GoogleCallback = lazyWithRetry(() => import('../pages/auth/GoogleCallback'));
const FacebookCallback = lazyWithRetry(() => import('../pages/auth/FacebookCallback'));
const AppleCallback = lazyWithRetry(() => import('../pages/auth/AppleCallback'));
const AppleProcessing = lazyWithRetry(() => import('../pages/auth/AppleProcessing'));
const LinkedInCallback = lazyWithRetry(() => import('../pages/auth/LinkedInCallback'));
const DebugLogin = lazyWithRetry(() => import('../pages/auth/DebugLogin'));
const DevAuth = lazyWithRetry(() => import('../pages/auth/DevAuth'));
const PendingApproval = lazyWithRetry(() => import('../pages/auth/PendingApproval'));
const OAuthConsent = lazyWithRetry(() => import('../pages/auth/OAuthConsent'));
const AccountSuspended = lazyWithRetry(() => import('../pages/AccountSuspended'));
const WrongEnvironment = lazyWithRetry(() => import('../pages/WrongEnvironment'));
const NotFound = lazyWithRetry(() => import('../pages/NotFound'));
const LoginPage = lazyWithRetry(() => import('../pages/auth/LoginPage'));
const SignupPage = lazyWithRetry(() => import('../pages/auth/SignupPage'));
const VerifyEmailPage = lazyWithRetry(() => import('../pages/auth/VerifyEmailPage'));
const MagicLinkPage = lazyWithRetry(() => import('../pages/auth/MagicLinkPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('../pages/auth/ResetPasswordPage'));

// New User Flow pages
const PlanSelection = lazyWithRetry(() => import('../pages/newuser/PlanSelection'));
const PlanComparison = lazyWithRetry(() => import('../pages/archive/compare-plans'));
const UnlimitedPlan = lazyWithRetry(() => import('../pages/archive/UnlimitedPlan'));
const UnlimitedPlanV2 = lazyWithRetry(() => import('../pages/archive/UnlimitedPlanV2'));
const PayGoPlan = lazyWithRetry(() => import('../pages/archive/PayGoPlan'));
const OneTimePlan = lazyWithRetry(() => import('../pages/archive/OneTimePlan'));
const NewUserAccountStep = lazyWithRetry(() => import('../pages/newuser/AccountStep'));
const NewUserPaymentConfirm = lazyWithRetry(() => import('../pages/newuser/PaymentConfirm'));
const NewUserPaymentSuccess = lazyWithRetry(() => import('../pages/newuser/PaymentSuccess'));
const GBPricesTest = lazyWithRetry(() => import('../pages/newuser/GBPricesTest'));
const GBPricesTestV2 = lazyWithRetry(() => import('../pages/newuser/GBPricesTestV2'));
const GBPricesTestV3 = lazyWithRetry(() => import('../pages/newuser/GBPricesTestV3'));

// Landing Pages (archived)
const UnlimitedTravelLanding = lazyWithRetry(() => import('../pages/archive/UnlimitedTravelLanding'));
const PayAsYouGoLanding = lazyWithRetry(() => import('../pages/archive/PayAsYouGoLanding'));
const OneTimePlanLanding = lazyWithRetry(() => import('../pages/archive/OneTimePlanLanding'));
const AllPlansLanding = lazyWithRetry(() => import('../pages/archive/AllPlansLanding'));

// Landing Pages
const BusinessPlansLanding = lazyWithRetry(() => import('../pages/landing/BusinessPlansLanding'));
const BusinessPlansLandingV2 = lazyWithRetry(() => import('../pages/landing/BusinessPlansLandingV2'));
const BusinessCompare = lazyWithRetry(() => import('../pages/landing/BusinessCompare'));
const SubscriptionPlansLanding = lazyWithRetry(() => import('../pages/landing/SubscriptionPlansLanding'));
const PayAsYouGoLandingV2 = lazyWithRetry(() => import('../pages/landing/PayAsYouGoLanding'));
const WorldCupLanding = lazyWithRetry(() => import('../pages/landing/WorldCupLanding'));
const EuropeLanding = lazyWithRetry(() => import('../pages/landing/EuropeLanding'));

// Alt Experimental Pages - DEV ONLY
const HomePageAlt00 = import.meta.env.DEV ? lazyWithRetry(() => import('../pages/alt00/HomePageAlt00')) : () => null;
const HomePageAlt01 = import.meta.env.DEV ? lazyWithRetry(() => import('../pages/alt01/HomePageAlt01')) : () => null;
const HomePageAlt02 = import.meta.env.DEV ? lazyWithRetry(() => import('../pages/alt02/HomePageAlt02')) : () => null;

// Country SEO Landing Pages
const HomePageCA = lazyWithRetry(() => import('../pages/country/HomePageCA'));
const HomePageAU = lazyWithRetry(() => import('../pages/country/HomePageAU'));

// Public Pages
const FAQ = lazyWithRetry(() => import('../pages/public/FAQ'));
const PrivacyPolicy = lazyWithRetry(() => import('../pages/public/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('../pages/public/TermsOfService'));
const RefundPolicy = lazyWithRetry(() => import('../pages/public/RefundPolicy'));
const Contact = lazyWithRetry(() => import('../pages/public/Contact'));
const Help = lazyWithRetry(() => import('../pages/public/Help'));
const HelpDefault = lazyWithRetry(() => import('../pages/public/HelpDefault'));
const HelpFrontend = lazyWithRetry(() => import('../pages/public/HelpFrontend'));
const BrandAssets = lazyWithRetry(() => import('../pages/public/BrandAssets'));
const Countries = lazyWithRetry(() => import('../pages/public/Countries'));
const CountriesExport = lazyWithRetry(() => import('../pages/public/CountriesExport'));
const News = lazyWithRetry(() => import('../pages/public/News'));
const NewsPost = lazyWithRetry(() => import('../pages/public/NewsPost'));
const RedeemCoupon = lazyWithRetry(() => import('../pages/RedeemCoupon'));
const ReferralLanding = lazyWithRetry(() => import('../pages/ReferralLanding'));
const AffiliateLanding = lazyWithRetry(() => import('../pages/public/AffiliateLanding'));
const AgentsOverview = lazyWithRetry(() => import('../pages/public/AgentsOverview'));
const EmailAgentLanding = lazyWithRetry(() => import('../pages/public/EmailAgentLanding'));
const Leaderboard = lazyWithRetry(() => import('../pages/public/Leaderboard'));
const WorldCupLeaderboard = lazyWithRetry(() => import('../pages/public/WorldCupLeaderboard'));
const ServiceLinkPage = lazyWithRetry(() => import('../pages/link/ServiceLinkPage'));
const AgentPayment = lazyWithRetry(() => import('../pages/public/AgentPayment'));

// Pixel Kids Watch pages
const PixelOrder = lazyWithRetry(() => import('../pages/pixel/PixelOrder'));
const PixelPayment = lazyWithRetry(() => import('../pages/pixel/PixelPayment'));
const PixelKycForm = lazyWithRetry(() => import('../pages/pixel/PixelKycForm'));
const PixelSubscriberVerify = lazyWithRetry(() => import('../pages/pixel/PixelSubscriberVerify'));
const PixelHistory = lazyWithRetry(() => import('../pages/pixel/PixelHistory'));
const PixelSuccess = lazyWithRetry(() => import('../pages/pixel/PixelSuccess'));
const PixelHandover = lazyWithRetry(() => import('../pages/pixel/PixelHandover'));
const PixelWearosEntry = lazyWithRetry(() => import('../pages/pixel/PixelWearosEntry'));
const PixelCameraTest = lazyWithRetry(() => import('../pages/pixel/PixelCameraTest'));
const PixelExternalNavTest = lazyWithRetry(() => import('../pages/pixel/PixelExternalNavTest'));
const PixelLayout = lazyWithRetry(() => import('../components/pixel/PixelLayout'));
const PixelHome = lazyWithRetry(() => import('../pages/pixel/dashboard/PixelHome'));
const PixelSubDetail = lazyWithRetry(() => import('../pages/pixel/dashboard/PixelSubDetail'));
const PixelBilling = lazyWithRetry(() => import('../pages/pixel/dashboard/PixelBilling'));
const PixelProfile = lazyWithRetry(() => import('../pages/pixel/dashboard/PixelProfile'));
const PixelDevice = lazyWithRetry(() => import('../pages/pixel/dashboard/PixelDevice'));
const PixelDeviceDetail = lazyWithRetry(() => import('../pages/pixel/dashboard/PixelDeviceDetail'));

// Business Account Pages
const BusinessMyEsims = lazyWithRetry(() => import('../pages/business/MemberHome'));
const BusinessMembers = lazyWithRetry(() => import('../pages/business/Members'));
const BusinessBilling = lazyWithRetry(() => import('../pages/business/Billing'));
const BusinessTransactions = lazyWithRetry(() => import('../pages/business/Transactions'));
const BusinessUsage = lazyWithRetry(() => import('../pages/business/Usage').then(m => ({ default: m.MemberUsageView })));
// Account Summary must stay role-aware: the admin view calls admin-only APIs
// (analytics, usage-by-country) that 403 for a plain member, and /business plus
// the sidebar logo send every role here. The default export picks the right view.
const BusinessSummary = lazyWithRetry(() => import('../pages/business/Usage'));
const BusinessSettings = lazyWithRetry(() => import('../pages/business/Settings'));
const BusinessAnalytics = lazyWithRetry(() => import('../pages/business/Analytics'));
const BusinessSupport = lazyWithRetry(() => import('../pages/business/Support'));
const BusinessSignup = lazyWithRetry(() => import('../pages/business/Signup'));
const BusinessLayout = lazyWithRetry(() => import('../components/layout/BusinessLayout'));
const BusinessProtectedRoute = lazyWithRetry(() => import('../components/common/BusinessProtectedRoute'));

// New Plan Flow Pages
const PlanSelectionV2 = lazyWithRetry(() => import('../pages/newplan/PlanSelection'));
const Premium = lazyWithRetry(() => import('../pages/newplan/Premium'));
const Solo = lazyWithRetry(() => import('../pages/newplan/Solo'));
const PayGo = lazyWithRetry(() => import('../pages/newplan/PayGo'));

// iPad optimized pages
const IpadSelectCarrier = lazyWithRetry(() => import('../pages/user/IpadSelectCarrier'));
const IpadIntro = lazyWithRetry(() => import('../pages/user/IpadIntro'));
const IpadPurchase = lazyWithRetry(() => import('../pages/user/IpadPurchase'));
const IpadPayment = lazyWithRetry(() => import('../pages/user/IpadPayment'));
const IpadActivation = lazyWithRetry(() => import('../pages/user/IpadActivation'));

// iPad v1 optimized pages (archived)
const IpadV1SelectCarrier = lazyWithRetry(() => import('../pages/archive/ipadv1/IpadV1SelectCarrier'));
const IpadV1Purchase = lazyWithRetry(() => import('../pages/archive/ipadv1/IpadV1Purchase'));
const IpadV1Payment = lazyWithRetry(() => import('../pages/archive/ipadv1/IpadV1Payment'));
const IpadV1Activation = lazyWithRetry(() => import('../pages/archive/ipadv1/IpadV1Activation'));

// Docs system - redirects to external docs.jetogo.com

// External redirect component
const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.href = to; }, [to]);
  return null;
};

// Suspense fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900">
    <div className="text-center">
      <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// Suspense wrapper helper
const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// Admin route wrapper — only available in non-public builds
const AdminRoute = !isPublicBuild
  ? ({ children }) => (
      <Suspense fallback={<PageLoader />}>
        <AdminProtectedRoute>
          <AdminLayout>{children}</AdminLayout>
        </AdminProtectedRoute>
      </Suspense>
    )
  : null;

const BlogSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/news/${slug}`} replace />;
};

const AppRoutes = () => {
  // Check if we're in a development environment based on domain (same pattern as Footer.jsx)
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLoopbackHost =
    currentDomain === '127.0.0.1' ||
    currentDomain === '::1' ||
    currentDomain === '[::1]' ||
    /^127(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}$/.test(currentDomain);

  const isDevelopment =
    import.meta.env.DEV ||
    currentDomain === 'localhost' ||
    currentDomain === 'jetogo.local' ||
    currentDomain === 'dev.jetogo.com' ||
    isLoopbackHost;

  return (
    <BrowserRouter>
      <ScrollToTop />
      <UserRecognitionWrapper>
        <Routes>
        {/* Public Home Page - Eager loaded */}
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />

        {/* Alt00 Experimental Pages - DEV ONLY */}
        {import.meta.env.DEV && (
          <Route path="/alt00" element={<PublicLayout><LazyRoute><HomePageAlt00 /></LazyRoute></PublicLayout>} />
        )}

        {/* Alt01 Experimental Pages - DEV ONLY */}
        {import.meta.env.DEV && (
          <Route path="/alt01" element={<PublicLayout><LazyRoute><HomePageAlt01 /></LazyRoute></PublicLayout>} />
        )}

        {/* Alt02 Experimental Pages - DEV ONLY */}
        {import.meta.env.DEV && (
          <Route path="/alt02" element={<PublicLayout><LazyRoute><HomePageAlt02 /></LazyRoute></PublicLayout>} />
        )}

        {/* Public Pages */}
        <Route path="/faq" element={<PublicLayout><LazyRoute><FAQ /></LazyRoute></PublicLayout>} />
        <Route path="/privacy" element={<PublicLayout><LazyRoute><PrivacyPolicy /></LazyRoute></PublicLayout>} />
        <Route path="/terms" element={<PublicLayout><LazyRoute><TermsOfService /></LazyRoute></PublicLayout>} />
        <Route path="/subprocessors" element={<PublicLayout><LazyRoute><TermsOfService /></LazyRoute></PublicLayout>} />
        <Route path="/refund-policy" element={<PublicLayout><LazyRoute><RefundPolicy /></LazyRoute></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><LazyRoute><Contact /></LazyRoute></PublicLayout>} />
        <Route path="/help" element={<PublicLayout><LazyRoute><Help /></LazyRoute></PublicLayout>} />
        <Route path="/help/default" element={<PublicLayout><LazyRoute><HelpDefault /></LazyRoute></PublicLayout>} />
        <Route path="/help/frontend" element={<PublicLayout><LazyRoute><HelpFrontend /></LazyRoute></PublicLayout>} />
        <Route path="/brand" element={<PublicLayout><LazyRoute><BrandAssets /></LazyRoute></PublicLayout>} />
        <Route path="/en-ca" element={<PublicLayout><LazyRoute><HomePageCA /></LazyRoute></PublicLayout>} />
        <Route path="/en-au" element={<PublicLayout><LazyRoute><HomePageAU /></LazyRoute></PublicLayout>} />
        <Route path="/countries" element={<PublicLayout><LazyRoute><Countries /></LazyRoute></PublicLayout>} />
        <Route path="/countries/:format" element={<LazyRoute><CountriesExport /></LazyRoute>} />
        <Route path="/leaderboard" element={<PublicLayout><LazyRoute><Leaderboard /></LazyRoute></PublicLayout>} />
        <Route path="/leaderboard/worldcup" element={<PublicLayout><LazyRoute><WorldCupLeaderboard /></LazyRoute></PublicLayout>} />
        <Route path="/redeem" element={<PublicHeaderLayout><LazyRoute><RedeemCoupon /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/affiliate" element={<PublicLayout><LazyRoute><AffiliateLanding /></LazyRoute></PublicLayout>} />
        <Route path="/agents" element={<PublicLayout><LazyRoute><AgentsOverview /></LazyRoute></PublicLayout>} />
        <Route path="/agents/email" element={<PublicLayout><LazyRoute><EmailAgentLanding /></LazyRoute></PublicLayout>} />

        {/* News/Blog Pages */}
        <Route path="/news" element={<PublicLayout><LazyRoute><News /></LazyRoute></PublicLayout>} />
        <Route path="/news/:slug" element={<PublicLayout><LazyRoute><NewsPost /></LazyRoute></PublicLayout>} />
        <Route path="/blog" element={<Navigate to="/news" replace />} />
        <Route path="/blog/:slug" element={<BlogSlugRedirect />} />

        {/* Referral Landing Page */}
        <Route path="/r/:code" element={<LazyRoute><ReferralLanding /></LazyRoute>} />

        {/* Third-Party Service Linking Page (Telegram, etc.) - Standalone, no layout */}
        <Route path="/link/:platform" element={<LazyRoute><ServiceLinkPage /></LazyRoute>} />

        {/* Agent Payment Page - Standalone, no layout, no auth */}
        <Route path="/pay" element={<LazyRoute><AgentPayment /></LazyRoute>} />

        {/* Pixel Kids Watch Flow */}
        <Route path="/pixel/wearos" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelWearosEntry /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/order" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelOrder /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/payment" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelPayment /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/kyc" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelKycForm /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/subscriber-verify" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelSubscriberVerify /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/success" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelSuccess /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/camera-test" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelCameraTest /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/external-nav-test" element={
          <LazyRoute>
            <PixelLayout mode="webview"><PixelExternalNavTest /></PixelLayout>
          </LazyRoute>
        } />
        <Route path="/pixel/history" element={
          <ProtectedRoute>
            <LazyRoute>
              <PixelLayout mode="webview"><PixelHistory /></PixelLayout>
            </LazyRoute>
          </ProtectedRoute>
        } />

        {/* Pixel WearOS dev entry (POST /v1/google/wearos/entry simulator) — see PixelHandover */}
        {isDevelopment && (
          <Route path="/pixel/start" element={
            <LazyRoute><PixelHandover /></LazyRoute>
          } />
        )}

        {/* Pixel Kids Watch Dashboard - Mobile Optimized */}
        <Route path="/pixel/dashboard" element={
          <ProtectedRoute>
            <LazyRoute>
              <PixelLayout><PixelHome /></PixelLayout>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/pixel/dashboard/sub/:id" element={
          <ProtectedRoute>
            <LazyRoute>
              <PixelLayout><PixelSubDetail /></PixelLayout>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/pixel/dashboard/billing" element={
          <ProtectedRoute>
            <LazyRoute>
              <PixelLayout><PixelBilling /></PixelLayout>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/pixel/dashboard/profile" element={
          <ProtectedRoute>
            <LazyRoute>
              <PixelLayout><PixelProfile /></PixelLayout>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/pixel/dashboard/device/:eid" element={
          <ProtectedRoute>
            <LazyRoute>
              <PixelLayout><PixelDeviceDetail /></PixelLayout>
            </LazyRoute>
          </ProtectedRoute>
        } />
        <Route path="/pixel/dashboard/device" element={
          <ProtectedRoute>
            <LazyRoute>
              <PixelLayout><PixelDevice /></PixelLayout>
            </LazyRoute>
          </ProtectedRoute>
        } />

        {/* Web Application Routes - Protected with Authentication */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MainLayout>
              <LazyRoute><Dashboard /></LazyRoute>
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/billing" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><BillingPayment /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/esims" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><EsimsPage /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><ProfileSettings /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/usage" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><UsageData /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/cookie-test" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><CookieTest /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/debug/auth" element={<LazyRoute><AuthDebug /></LazyRoute>} />
        <Route path="/debug/localstorage" element={<LazyRoute><LocalStorageDebug /></LazyRoute>} />
        <Route path="/dashboard/wrapped" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><Wrapped /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/referrals" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><Referrals /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/speedtest" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><SpeedTest /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/speedtest/history" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><SpeedTestHistory /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/game/worldcup" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><WorldCupGame /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/feature-requests" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><FeatureRequests /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/feature-requests/*" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><FeatureRequests /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/archive/purchase" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><PurchasePlan /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/payment" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><PaymentConfirm /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/success" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><PurchaseSuccess /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />

        {/* Auth Routes */}
        <Route path="/login" element={<PublicHeaderLayout><LazyRoute><LoginPage /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/signup" element={<PublicHeaderLayout><LazyRoute><SignupPage /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/auth/verify-email" element={<PublicLayout><LazyRoute><VerifyEmailPage /></LazyRoute></PublicLayout>} />
        <Route path="/auth/magic-link" element={<PublicLayout><LazyRoute><MagicLinkPage /></LazyRoute></PublicLayout>} />
        <Route path="/forgot-password" element={<PublicHeaderLayout><LazyRoute><ForgotPasswordPage /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/auth/reset-password" element={<PublicHeaderLayout><LazyRoute><ResetPasswordPage /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/debug-login" element={<LazyRoute><DebugLogin /></LazyRoute>} />
        <Route path="/auth/google/callback" element={<PublicLayout><LazyRoute><GoogleCallback /></LazyRoute></PublicLayout>} />
        <Route path="/auth/facebook/callback" element={<PublicLayout><LazyRoute><FacebookCallback /></LazyRoute></PublicLayout>} />
        <Route path="/auth/apple/callback" element={<PublicLayout><LazyRoute><AppleCallback /></LazyRoute></PublicLayout>} />
        <Route path="/auth/apple/processing" element={<PublicLayout><LazyRoute><AppleProcessing /></LazyRoute></PublicLayout>} />
        <Route path="/auth/linkedin/callback" element={<PublicLayout><LazyRoute><LinkedInCallback /></LazyRoute></PublicLayout>} />
        <Route path="/auth/oauth/authorize" element={<LazyRoute><OAuthConsent /></LazyRoute>} />
        <Route path="/auth/pending-approval" element={<LazyRoute><PendingApproval /></LazyRoute>} />

        {/* Account Suspended Page - for suspended users */}
        <Route path="/account-suspended" element={<LazyRoute><AccountSuspended /></LazyRoute>} />

        {/* Wrong Environment Page - for accounts blocked by the environment gate */}
        <Route path="/wrong-environment" element={<LazyRoute><WrongEnvironment /></LazyRoute>} />

        {/* Marketing Landing Pages (archived) */}
        <Route path="/archive/landing/unlimited-travel" element={<LazyRoute><UnlimitedTravelLanding /></LazyRoute>} />
        <Route path="/archive/landing/pay-as-you-go" element={<LazyRoute><PayAsYouGoLanding /></LazyRoute>} />
        <Route path="/archive/landing/one-time-plan" element={<LazyRoute><OneTimePlanLanding /></LazyRoute>} />
        <Route path="/archive/landing/all-plans" element={<LazyRoute><AllPlansLanding /></LazyRoute>} />

        {/* Marketing Landing Pages */}
        <Route path="/landing/business-plans" element={<PublicLayout><LazyRoute><BusinessPlansLandingV2 /></LazyRoute></PublicLayout>} />
        <Route path="/landing/business-plans-v1" element={<PublicLayout><LazyRoute><BusinessPlansLanding /></LazyRoute></PublicLayout>} />
        <Route path="/landing/business-plans-v2" element={<PublicLayout><LazyRoute><BusinessPlansLandingV2 /></LazyRoute></PublicLayout>} />
        <Route path="/landing/business-plans/compare" element={<PublicLayout><LazyRoute><BusinessCompare /></LazyRoute></PublicLayout>} />
        <Route path="/landing/subscription-plans" element={<PublicLayout><LazyRoute><SubscriptionPlansLanding /></LazyRoute></PublicLayout>} />
        <Route path="/landing/pay-as-you-go" element={<PublicLayout><LazyRoute><PayAsYouGoLandingV2 /></LazyRoute></PublicLayout>} />
        <Route path="/landing/worldcup" element={<PublicLayout><LazyRoute><WorldCupLanding /></LazyRoute></PublicLayout>} />
        <Route path="/landing/europe" element={<PublicLayout><LazyRoute><EuropeLanding /></LazyRoute></PublicLayout>} />
        {/* Redirect all-plans to the home page */}
        <Route path="/landing/all-plans" element={<Navigate to="/#comparison" replace />} />

        {/* New User Flow Routes - Using NoFooterLayout */}
        <Route path="/newuser" element={<Navigate to="/newplan" replace />} />
        <Route path="/archive/compare-plans" element={<NoFooterLayout><LazyRoute><PlanComparison /></LazyRoute></NoFooterLayout>} />
        <Route path="/archive/unlimited" element={<NoFooterLayout><LazyRoute><UnlimitedPlan /></LazyRoute></NoFooterLayout>} />
        <Route path="/archive/unlimitedv2" element={<NoFooterLayout><LazyRoute><UnlimitedPlanV2 /></LazyRoute></NoFooterLayout>} />
        <Route path="/archive/paygo" element={<NoFooterLayout><LazyRoute><PayGoPlan /></LazyRoute></NoFooterLayout>} />
        <Route path="/archive/onetime" element={<NoFooterLayout><LazyRoute><OneTimePlan /></LazyRoute></NoFooterLayout>} />
        <Route path="/newuser/account" element={<NoFooterLayout><LazyRoute><NewUserAccountStep /></LazyRoute></NoFooterLayout>} />
        <Route path="/newuser/payment" element={<NoFooterLayout><LazyRoute><NewUserPaymentConfirm /></LazyRoute></NoFooterLayout>} />
        <Route path="/newuser/success" element={<NoFooterLayout><LazyRoute><NewUserPaymentSuccess /></LazyRoute></NoFooterLayout>} />
        <Route path="/newuser/gbpricesv1" element={<NoFooterLayout><LazyRoute><GBPricesTest /></LazyRoute></NoFooterLayout>} />
        <Route path="/newuser/gbpricesv2" element={<NoFooterLayout><LazyRoute><GBPricesTestV2 /></LazyRoute></NoFooterLayout>} />
        <Route path="/newuser/gbpricesv3" element={<NoFooterLayout><LazyRoute><GBPricesTestV3 /></LazyRoute></NoFooterLayout>} />

        {/* New Plan Flow Routes - Using PublicHeaderLayout (header only, no footer) */}
        <Route path="/newplan" element={<PublicHeaderLayout><LazyRoute><PlanSelectionV2 /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/newplan/premium" element={<PublicHeaderLayout><LazyRoute><Premium /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/newplan/solo" element={<PublicHeaderLayout><LazyRoute><Solo /></LazyRoute></PublicHeaderLayout>} />
        <Route path="/newplan/paygo" element={<PublicHeaderLayout><LazyRoute><PayGo /></LazyRoute></PublicHeaderLayout>} />

        {/* iPad Optimized Routes - DEV ONLY */}
        {import.meta.env.DEV && (
          <>
            <Route path="/ipad" element={<LazyRoute><IpadSelectCarrier /></LazyRoute>} />
            <Route path="/ipad/intro" element={<LazyRoute><IpadIntro /></LazyRoute>} />
            <Route path="/ipad/purchase" element={<LazyRoute><IpadPurchase /></LazyRoute>} />
            <Route path="/ipad/payment" element={<LazyRoute><IpadPayment /></LazyRoute>} />
            <Route path="/ipad/activate" element={<LazyRoute><IpadActivation /></LazyRoute>} />
          </>
        )}

        {/* iPad V1 Optimized Routes - DEV ONLY */}
        {import.meta.env.DEV && (
          <>
            <Route path="/ipadv1" element={<LazyRoute><IpadV1SelectCarrier /></LazyRoute>} />
            <Route path="/ipadv1/purchase" element={<LazyRoute><IpadV1Purchase /></LazyRoute>} />
            <Route path="/ipadv1/payment" element={<LazyRoute><IpadV1Payment /></LazyRoute>} />
            <Route path="/ipadv1/activate" element={<LazyRoute><IpadV1Activation /></LazyRoute>} />
          </>
        )}

        {/* Dev Auth - Auto-login via URL for browser agents - DEV ONLY */}
        {isDevelopment && (
          <Route path="/dev-auth" element={<LazyRoute><DevAuth /></LazyRoute>} />
        )}

        {/* Business Account Routes */}
        <Route path="/business/signup" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessSignup />
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business" element={<Navigate to="/business/summary" replace />} />
        <Route path="/business/esims" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessMyEsims /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/members" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessMembers /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/billing" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessBilling /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/transactions" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessTransactions /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/usage" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessUsage /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/summary" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessSummary /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/analytics" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessAnalytics /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/settings" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessSettings /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />
        <Route path="/business/support" element={
          <LazyRoute>
            <Suspense fallback={<PageLoader />}>
              <BusinessProtectedRoute>
                <BusinessLayout><BusinessSupport /></BusinessLayout>
              </BusinessProtectedRoute>
            </Suspense>
          </LazyRoute>
        } />

        {/* Admin Routes - Only included in non-public builds.
            Each admin lazy import is gated by !isPublicBuild, so Vite tree-shakes
            all admin code from public builds at compile time. */}
        {!isPublicBuild && (
          <>
            <Route path="/admin" element={
              <AdminRoute><LazyRoute><AdminDashboard /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute><LazyRoute><UserManagement /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/users/:userId" element={
              <AdminRoute><LazyRoute><UserDetail /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/plans" element={<Navigate to="/admin/settings/plans" replace />} />
            <Route path="/admin/country-pricing" element={
              <AdminRoute><LazyRoute><CountryPricing /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/country-pricing-rates" element={
              <AdminRoute><LazyRoute><CountryPricingRates /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/plan-offers" element={
              <Navigate to="/admin/settings/plans" replace />
            } />
            <Route path="/admin/esims" element={
              <AdminRoute><LazyRoute><ESIMManagement /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/pixel-devices" element={
              <AdminRoute><LazyRoute><PixelDeviceSearch /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/e2e-results" element={
              <AdminRoute><LazyRoute><E2EResults /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/esims/report" element={
              <AdminRoute><LazyRoute><ESIMReport /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/esims/report/:esimNumber" element={
              <AdminRoute><LazyRoute><ESIMReport /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/esims/monthly-report/:yearMonth" element={
              <AdminRoute><LazyRoute><MonthlyReportDetail /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/esims/plmn-directory" element={
              <AdminRoute><LazyRoute><PLMNDirectory /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/cdr" element={
              <AdminRoute><LazyRoute><CDRMonitoring /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/fraud-monitor" element={
              <AdminRoute><LazyRoute><FraudMonitor /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/orders" element={
              <AdminRoute><LazyRoute><OrderManagement /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/subscriptions" element={
              <AdminRoute><LazyRoute><SubscriptionManagement /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/analytics" element={
              <AdminRoute><LazyRoute><Analytics /></LazyRoute></AdminRoute>
            } />

            {/* Bonus Programs routes */}
            <Route path="/admin/bonuses" element={
              <AdminRoute><LazyRoute><BonusPrograms /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/coupons" element={
              <AdminRoute><LazyRoute><BonusPrograms /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/affiliate" element={
              <AdminRoute><LazyRoute><BonusPrograms /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/referrals" element={
              <AdminRoute><LazyRoute><BonusPrograms /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/logs" element={
              <AdminRoute><LazyRoute><BonusPrograms /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/simulator" element={
              <AdminRoute><LazyRoute><BonusSimulator /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/new" element={
              <AdminRoute><LazyRoute><BonusProgramForm /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/:id" element={
              <AdminRoute><LazyRoute><BonusProgramForm /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/:id/edit" element={
              <AdminRoute><LazyRoute><BonusProgramForm /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/:id/stats" element={
              <AdminRoute><LazyRoute><BonusProgramStats /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/referral" element={
              <AdminRoute><LazyRoute><ReferralManagement /></LazyRoute></AdminRoute>
            } />

            {/* Voucher Codes */}
            <Route path="/admin/bonuses/voucher" element={
              <AdminRoute><LazyRoute><BonusPrograms /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/bonuses/vouchers" element={
              <AdminRoute><LazyRoute><BonusPrograms /></LazyRoute></AdminRoute>
            } />

            {/* Mail system routes */}
            <Route path="/admin/mail" element={
              <AdminRoute><LazyRoute><Mail /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/mail/configuration" element={
              <AdminRoute><LazyRoute><MailConfiguration /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/mail/templates" element={
              <AdminRoute><LazyRoute><MailTemplates /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/mail/triggers" element={
              <AdminRoute><LazyRoute><MailTriggers /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/mail/wrapper" element={
              <AdminRoute><LazyRoute><MailWrapper /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/mail/recent" element={
              <AdminRoute><LazyRoute><MailRecent /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/mail/queue" element={
              <AdminRoute><LazyRoute><MailQueue /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/mail/support" element={
              <AdminRoute><LazyRoute><MailSupport /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/settings/supportmail" element={
              <AdminRoute><LazyRoute><ZohoMailSettings /></LazyRoute></AdminRoute>
            } />

            {/* Settings with tab routing */}
            <Route path="/admin/settings" element={
              <AdminRoute><LazyRoute><Settings /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/settings/:tab" element={
              <AdminRoute><LazyRoute><Settings /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/database" element={
              <AdminRoute><LazyRoute><DatabaseManagement /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/database/endpoints" element={
              <AdminRoute><LazyRoute><DatabaseManagement initialTab="endpoints" /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/database/query" element={
              <AdminRoute><LazyRoute><DatabaseQuery /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/api-docs" element={
              <AdminRoute><LazyRoute><ApiDocumentation /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/oauth-clients" element={
              <AdminRoute><LazyRoute><OAuthClients /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/docs" element={
              <AdminRoute><LazyRoute><DocsAdmin /></LazyRoute></AdminRoute>
            } />

            {/* Unified Logs page with tabs */}
            <Route path="/admin/logs" element={
              <AdminRoute><LazyRoute><Logs /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/logs/error" element={
              <AdminRoute><LazyRoute><Logs /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/logs/audit" element={
              <AdminRoute><LazyRoute><Logs /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/logs/signals" element={
              <AdminRoute><LazyRoute><Logs /></LazyRoute></AdminRoute>
            } />

            {/* Legacy routes - redirect to new unified logs page */}
            <Route path="/admin/eventlogs" element={<Navigate to="/admin/logs" replace />} />
            <Route path="/admin/errorlogs" element={<Navigate to="/admin/logs/error" replace />} />
            <Route path="/admin/auditlogs" element={<Navigate to="/admin/logs/audit" replace />} />

            {/* AI Knowledge Routes */}
            <Route path="/admin/knowledge" element={
              <AdminRoute><LazyRoute><AIKnowledgeDashboard /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/knowledge/chat-logs" element={
              <AdminRoute><LazyRoute><ChatLogs /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/knowledge/knowledge-base" element={
              <AdminRoute><LazyRoute><KnowledgeBase /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/knowledge/analysis" element={
              <AdminRoute><LazyRoute><ChatAnalysis /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/knowledge/storage" element={
              <AdminRoute><LazyRoute><OpenAIStorage /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/knowledge/auth-sessions" element={
              <AdminRoute><LazyRoute><AuthenticatedSessions /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/knowledge/functions" element={
              <AdminRoute><LazyRoute><AIFunctionDocs /></LazyRoute></AdminRoute>
            } />

            <Route path="/admin/testing" element={
              <AdminRoute><LazyRoute><TestFunctions /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/testing/connectcdr" element={
              <AdminRoute><LazyRoute><EsimCdrConnector /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/testing/userreset" element={
              <AdminRoute><LazyRoute><UserReset /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/testing/cdrview" element={
              <AdminRoute><LazyRoute><CdrView /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/testing/subscription" element={
              <AdminRoute><LazyRoute><SubscriptionTesting /></LazyRoute></AdminRoute>
            } />
            <Route path="/admin/testing/test_clocks" element={
              <AdminRoute><LazyRoute><TestClocks /></LazyRoute></AdminRoute>
            } />

            {/* Platform Updates */}
            <Route path="/admin/platformupdates" element={
              <AdminRoute><LazyRoute><PlatformUpdates /></LazyRoute></AdminRoute>
            } />
          </>
        )}

        {/* Legacy routes redirects - Also protected */}
        <Route path="/user" element={
          <ProtectedRoute>
            <MainLayout>
              <LazyRoute><Dashboard /></LazyRoute>
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><BillingPayment /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/usage" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><UsageData /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/purchase" element={
          <ProtectedRoute>
            <MainLayout><LazyRoute><PurchasePlan /></LazyRoute></MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/devices" element={<Navigate to="/dashboard/esims" replace />} />

        {/* Documentation - redirect to external docs site */}
        <Route path="/docs/*" element={<ExternalRedirect to="https://docs.jetogo.com/docs/" />} />

        {/* 404 Catch-all Route - Must be last */}
        <Route path="*" element={<PublicLayout><LazyRoute><NotFound /></LazyRoute></PublicLayout>} />
        </Routes>
      </UserRecognitionWrapper>
    </BrowserRouter>
  );
};

export default AppRoutes;
